import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, animate, motion, useMotionValue } from 'framer-motion';
import { QuestShard } from './QuestShard';
import { useQuest } from '../hooks/useQuest';
import { quest } from '../utils/quest';
interface QuestDragRevealProps {
  /** Hiding spot id (also set as `data-quest-spot`) */
  spot: string;
  children: React.ReactNode;
  className?: string;
  /** How far (px) it has to be dragged before the shard is uncovered */
  reveal?: number;
  /** Size of the shard underneath (px) */
  shardSize?: number;
  /**
   * Properly hidden: no glow peeking out from behind, no grab hand on hover — nothing gives it
   * away until you've actually dragged it aside
   */
  concealed?: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Hides a memory shard behind any element. While its shard is live the element can be grabbed and
 * dragged aside to uncover it (and it never fires its own click after a drag). Once the shard is
 * picked up, the element glides back home. Otherwise it's a plain wrapper.
 */
export function QuestDragReveal({
  spot,
  children,
  className = '',
  reveal = 56,
  shardSize = 44,
  concealed = false
}: QuestDragRevealProps) {
  useQuest(); // re-render on quest changes
  const on = quest.spotLive(spot);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [uncovered, setUncovered] = useState(false);
  const draggedRef = useRef(false);
  const goHome = () => {
    animate(x, 0, {
      duration: 0.6,
      ease: EASE
    });
    animate(y, 0, {
      duration: 0.6,
      ease: EASE
    });
  };

  // Shard gone (or quest left mid-drag): back home
  useEffect(() => {
    if (on) return;
    setUncovered(false);
    if (x.get() !== 0 || y.get() !== 0) goHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // Links/images inside would start the browser's own drag ghost instead of moving
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !on) return;
    const block = (e: DragEvent) => e.preventDefault();
    el.addEventListener('dragstart', block);
    return () => el.removeEventListener('dragstart', block);
  }, [on]);
  return <div ref={wrapRef} data-quest-spot={spot} className={`relative ${className}`} data-id="element-200" data-mp="zsmt993to0t7">
      <AnimatePresence data-id="element-201" data-mp="14ry8gdojiy8o">
        {on && (!concealed || uncovered) && <div className="pointer-events-none absolute inset-0 z-0 grid place-items-center" data-id="element-202" data-mp="r1clodvyu45i">
            <QuestShard size={shardSize} active={uncovered} className="pointer-events-auto" onCollect={() => {
          quest.collect(spot);
          setUncovered(false);
          goHome();
        }} data-id="element-203" data-mp="vs2s7ug4od1s" />
          </div>}
      </AnimatePresence>
      <motion.div className={`relative z-10 ${on ? concealed ? 'touch-none' : 'cursor-grab touch-none active:cursor-grabbing' : ''}`} style={{
      x,
      y
    }} drag={on} dragMomentum={false} dragElastic={0.12} onDragStart={() => {
      draggedRef.current = true;
      // Flag for the site-wide "external links open in a new tab" listener, which runs in the
      // capture phase before this element's own click guard — it skips links inside a drag
      wrapRef.current?.setAttribute('data-quest-dragging', 'true');
    }} onDrag={() => {
      if (!uncovered && Math.hypot(x.get(), y.get()) > reveal) setUncovered(true);
    }} onDragEnd={() => {
      // Let the click that ends a drag pass by first, then allow normal clicks again
      window.setTimeout(() => {
        draggedRef.current = false;
        wrapRef.current?.removeAttribute('data-quest-dragging');
      }, 60);
    }} onClickCapture={e => {
      if (on && draggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    }} data-id="element-204" data-mp="83jols80wog1">
        {children}
      </motion.div>
    </div>;
}